import { Component, OnInit } from '@angular/core';
import { ThemeService } from '../service/theme.service';
import { FilterService } from '../service/filter.service';
import { Operator, OperatorService } from '../service/operator.service';
import { ValidationService } from '../service/validation.service';
import { SystemMessageService } from '../service/system-message.service';
import { Client, ClientService } from '../service/client.service';
import { firstValueFrom } from 'rxjs';

interface OperatorClientSelection {
  client_id: string;
  client_name: string;
  checked: boolean;
}

@Component({
    selector: 'app-operator-list',
    templateUrl: './operator-list.component.html',
    styleUrls: ['./operator-list.component.css'],
    standalone: false
})
export class OperatorListComponent implements OnInit {

  // Variables used for HTML whit ngIf for the showing or not of block of html cde
  public mainContent: string = 'operator-list';
  public fromComponent: string = '';

  // Variable used in HTMl for the active status of
  public active: number = 0;

  // Variables used for the filter service
  public userFilter: string = '';
  public filterBy: string = '';
  public filterOptions = [
    { label: 'binName', value: 'bin_name' },
    { label: 'bin_model_number', value: 'bin_model_number' },
    { label: 'adminViewBinUsageNavBar', value: 'bin_usage' },
  ];
  public showClientAssignmentModal: boolean = false;
  public loadingClientAssignment: boolean = false;
  public savingClientAssignment: boolean = false;
  public clientAssignmentSearch: string = '';
  public selectedOperator: Operator | null = null;
  public clientSelections: OperatorClientSelection[] = [];

  constructor(public theme: ThemeService,
              public filter: FilterService,
              public operator: OperatorService,
              public clientService: ClientService,
              public validationService: ValidationService,
              private systemMessageService: SystemMessageService){

  }

  ngOnInit(): void {
    // Call the function that init the list
    this.initList();

    // Function that is automatically trigger used by bin service when there is a change made from bin-create or bin-update component
    this.operator.changeMade$.subscribe(async () => {
      // Call the function that init the list
      this.initList();
      this.systemMessageService.selectRibbon('success', 'alert-success-generic-message');
    });

    this.operator.noChangeMade$.subscribe(async () => {
      this.systemMessageService.selectRibbon('danger', 'alert-danger-generic-message');
    });
  }

  // Function that will initializa the lists
  async initList(){
    // Initialize Bin array in bin.service
    await this.operator.getOperators();
   
  }

  // Takes a sequence of numbers as input and formats it as a CA phone number in the format "(XXX) XXX-XXXX".
  formatarNumero(numberSequence: string | undefined | null): string {
    // If the input is undefined or null, return an empty string (or handle the case appropriately)
    if (numberSequence === undefined || numberSequence === null) {
        return ''; // or handle the case appropriately
    }

    // If the length of the cleaned number is 10, format it as (XXX) XXX-XXXX
    const justNumbers = numberSequence.replace(/\D/g, '');

    if (justNumbers.length === 10) {
        const parte1 = justNumbers.slice(0, 3);
        const parte2 = justNumbers.slice(3, 6);
        const parte3 = justNumbers.slice(6);
        return `(${parte1}) ${parte2}-${parte3}`;
    } else {
      // If the length is not 10, return the input as is
        return numberSequence;
    }
  }

  // Function used to made a search in the list
  search(){
    // Check if there's a filterBy variable and set it to it's default value
    if(this.filterBy === ''){
      this.filterBy = 'operator_name';
    }

    this.operator.operator_array = this.filter.search(this.userFilter, this.operator.operator_array, this.operator.original_operator_array, this.filterBy);
  }

  // Filters the modal tag list without mutating the underlying selection state.
  get filteredClientSelections(): OperatorClientSelection[] {
    const search = this.clientAssignmentSearch.trim().toLowerCase();

    if (!search) {
      return this.clientSelections;
    }

    return this.clientSelections.filter((client) =>
      client.client_name.toLowerCase().includes(search)
    );
  }

  // Opens the assignment modal and loads both the full client list and the current operator links.
  async openClientAssignmentModal(operator: Operator) {
    this.showClientAssignmentModal = true;
    this.loadingClientAssignment = true;
    this.clientAssignmentSearch = '';
    this.selectedOperator = operator;
    this.clientSelections = [];

    try {
      const clients = await firstValueFrom(this.clientService.getClients()) as Client[];
      const assignedClientIds = await this.operator.fetchOperatorClientIds(operator.operator_id);

      // A Set keeps the "already assigned" check fast while mapping all clients for the UI.
      const selectedIds = new Set(assignedClientIds);
      const validClients = Array.isArray(clients) ? clients : [];

      this.clientSelections = validClients
        .filter((client) => client.client_id && client.client_name)
        .map((client) => ({
          client_id: client.client_id as string,
          client_name: client.client_name,
          checked: selectedIds.has(client.client_id as string)
        }))
        .sort((a, b) => a.client_name.localeCompare(b.client_name));
    } catch (error) {
      console.error('Failed to load operator clients', error);
      this.systemMessageService.selectRibbon('danger', 'alert-danger-generic-message');
      this.closeClientAssignmentModal();
    } finally {
      this.loadingClientAssignment = false;
    }
  }

  // Closes the modal and clears the temporary operator-client selection state.
  closeClientAssignmentModal() {
    this.showClientAssignmentModal = false;
    this.loadingClientAssignment = false;
    this.savingClientAssignment = false;
    this.clientAssignmentSearch = '';
    this.selectedOperator = null;
    this.clientSelections = [];
  }

  // Persists the checked client ids as the full source of truth for this operator.
  async saveClientAssignments() {
    if (!this.selectedOperator || this.savingClientAssignment) {
      return;
    }

    this.savingClientAssignment = true;

    // The backend endpoint expects the complete replacement list, not incremental diffs.
    const client_ids = this.clientSelections
      .filter((client) => client.checked)
      .map((client) => client.client_id);

    try {
      const response: any = await firstValueFrom(
        this.operator.setOperatorClients(this.selectedOperator.operator_id, client_ids)
      );

      if (response?.message === 'success') {
        this.systemMessageService.selectRibbon('success', 'alert-success-generic-message');
        this.closeClientAssignmentModal();
      } else {
        this.systemMessageService.selectRibbon('danger', 'alert-danger-generic-message');
      }
    } catch (error) {
      console.error('Failed to save operator clients', error);
      this.systemMessageService.selectRibbon('danger', 'alert-danger-generic-message');
    } finally {
      this.savingClientAssignment = false;
    }
  }
}
